/**
 * «Asignación inicial» — what the app's five pages share about the URL (bata-platform#939 §8,
 * `docs/adr/5001`): the paths, the keys and their pure parses. No React, no request.
 *
 * **The URL is the question** (CLAUDE.md §3.4). The market is `?market=`; the run the page reads is
 * `?initialRunId=` (its own key: an in-season cycle run's id would name no run of this app, and the
 * address bar carries every key across screens); a chosen line is `?row=`; a link may narrow the lines
 * to one article or store (`?f.article=`, `?f.store=`), and the lines page to one reason
 * (`?f.reason=`, the queue's `?queue.f.reason=`).
 *
 * No default but the two the rules grant (ADR 9056, ADR 9039): the only market the person sees, and the
 * newest run of the kind (`RunPicker`). A date, a wave or an article is never defaulted.
 */

/** The five pages (`manifest.ts`). */
export const ARTICLES_PATH = '/launch/initial';
export const SIMULATE_PATH = '/launch/initial/simulate';
export const RUNS_PATH = '/launch/initial/runs';
export const LINES_PATH = '/launch/initial/lines';
export const APPROVALS_PATH = '/launch/initial/approvals';

export const MARKET_PARAM = 'market';
/** The run a run page reads. Its own key: an in-season cycle run's id would name no run of this app. */
export const RUN_ID_PARAM = 'initialRunId';
/**
 * One view of a page, in the header chips' own word «view»:
 *   the articles page   `all` (also the articles that are not to allocate), `notAllocable` (a card's list, ADR 9069)
 *   the runs page       `served`, `leftOut` (the cells a card opens, ADR 9069)
 */
export const VIEW_PARAM = 'view';
export const VIEW_ALL = 'all';
export const VIEW_NOT_ALLOCABLE = 'notAllocable';
export const VIEW_SERVED = 'served';
export const VIEW_LEFT_OUT = 'leftOut';
export const ARTICLE_FILTER_PARAM = 'f.article';
/** The store a link narrows the lines to (the chips' own «store»). */
export const STORE_PARAM = 'store';
export const REASON_FILTER_PARAM = 'f.reason';
/** The decision queue's own reason filter, in the template's namespace (`queue.f.<id>`). */
export const QUEUE_REASON_FILTER = 'reason';
/** The articles of a run by verdict, on the runs page (the chips' own «state»). */
export const STATE_PARAM = 'state';
/** The article whose hold-back comparison the runs page opens. */
export const ARTICLE_OPEN_PARAM = 'article';

/** Rows a page of any list draws (ADR 9034's fifty). */
export const PAGE_SIZE = 50;

/** The memory scope of the newest-run default (DEC-99, `docs/adr/9039`): the live runs of the app. */
export const INITIAL_RUN_SCOPE = { kind: 'launch.initial_run' } as const;

function text(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim() ?? '';
  return value === '' ? null : value;
}

/** The market a link names, or `null`. Pure. */
export function readMarket(search: string): string | null {
  return text(new URLSearchParams(search), MARKET_PARAM);
}

/** The run a link names, or `null`. Pure. */
export function readRunId(search: string): string | null {
  return text(new URLSearchParams(search), RUN_ID_PARAM);
}

/** The view the address names, or `null`. Pure. */
export function readView(search: string): string | null {
  return text(new URLSearchParams(search), VIEW_PARAM);
}

/** Whether the articles page also lists the articles that are not to allocate. Pure. */
export function readExcluded(search: string): boolean {
  return readView(search) === VIEW_ALL;
}

/** Whether a card narrowed the articles list to the articles that cannot be allocated yet. Pure. */
export function readNotAllocable(search: string): boolean {
  return readView(search) === VIEW_NOT_ALLOCABLE;
}

export interface LineFilters {
  readonly article: string | null;
  readonly store: string | null;
  readonly reason: string | null;
}

/** The narrowing of the lines a link or the bar set: the article, the store, the reason that bound them. Pure. */
export function readLineFilters(search: string): LineFilters {
  const params = new URLSearchParams(search);
  return {
    article: text(params, ARTICLE_FILTER_PARAM),
    store: text(params, STORE_PARAM),
    // The queue's template keeps its own bar filter under `queue.f.reason`; a page of lines uses `f.reason`.
    reason: text(params, REASON_FILTER_PARAM) ?? text(params, `queue.f.${QUEUE_REASON_FILTER}`),
  };
}

/** The `?outcome=` the runs page narrows its articles to; an unknown word is no narrowing. Pure. */
export function readOutcome(search: string): 'allocated' | 'not_allocable' | 'not_selected' | null {
  const value = text(new URLSearchParams(search), STATE_PARAM);
  return value === 'allocated' || value === 'not_allocable' || value === 'not_selected' ? value : null;
}

/** The cells a card opened, or `null`. Pure. */
export function readCells(search: string): 'served' | 'leftOut' | null {
  const value = readView(search);
  return value === VIEW_SERVED || value === VIEW_LEFT_OUT ? value : null;
}

/** The article the runs page has open, or `null`. Pure. */
export function readOpenArticle(search: string): string | null {
  return text(new URLSearchParams(search), ARTICLE_OPEN_PARAM);
}

/** `path` with the keys set (and the empty ones left out), in the order given. Pure. */
export function pathWith(path: string, keys: Readonly<Record<string, string | null | undefined>>): string {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(keys)) {
    if (value !== null && value !== undefined && value !== '') params.set(name, value);
  }
  const query = params.toString();
  return query === '' ? path : `${path}?${query}`;
}

/** A page of the app on a run (and its market), with the extra keys. Pure. */
export function runHref(path: string, runId: string, extra: Readonly<Record<string, string | null | undefined>> = {}): string {
  return pathWith(path, { initialRunId: runId, ...extra });
}

/** A page of the app on a market, with the extra keys. Pure. */
export function marketHref(path: string, market: string | null, extra: Readonly<Record<string, string | null | undefined>> = {}): string {
  return pathWith(path, { [MARKET_PARAM]: market, ...extra });
}

/** The keys a page of the app carries to the next: the market and the run, nothing else. Pure. */
export function carried(search: string): { readonly market: string | null; readonly runId: string | null } {
  return { market: readMarket(search), runId: readRunId(search) };
}
